import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FilePlus2, FileText, Package, Boxes, BookUser, HandCoins, Truck, ClipboardList,
  Building2, CalendarDays, ScanBarcode, LogOut, Menu, Zap, FileClock, History, Archive, Wallet, TrendingUp, Users, Settings as SettingsIcon,
  ChevronDown, User,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { bn } from "../lib/format";
import { Modal } from "./kit";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const z = { timeZone: "Asia/Dhaka" } as const;
  const time = bn(now.toLocaleTimeString("en-US", { ...z, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }));
  const date = now.toLocaleDateString("bn-BD", { ...z, day: "numeric", month: "long", year: "numeric" });
  const weekday = now.toLocaleDateString("bn-BD", { ...z, weekday: "long" });
  return (
    <div className="flex items-center gap-2.5 px-3 md:px-4 py-1.5 md:py-2 rounded-xl bg-white/5 ring-1 ring-white/10" data-testid="header-live-clock">
      <CalendarDays size={18} className="text-amber-300 shrink-0 hidden sm:block" />
      <div className="leading-tight text-right sm:text-left">
        <p className="text-white font-bold text-[12.5px] md:text-[14px] tabular-nums" data-testid="header-clock-time">{time}</p>
        <p className="text-blue-200/70 text-[10px] md:text-[11px] whitespace-nowrap">{weekday}, {date}</p>
      </div>
    </div>
  );
}

export const NAV = [
  { to: "/", label: "ড্যাশবোর্ড", icon: LayoutDashboard, id: "dashboard" },
  { to: "/memo/new", label: "নতুন মেমো", icon: FilePlus2, id: "memo-create" },
  { to: "/memos", label: "মেমো তালিকা", icon: FileText, id: "memos" },
  { to: "/barcode-sale", label: "বারকোড সেল", icon: ScanBarcode, id: "barcode-sale" },
  { to: "/products", label: "পণ্য তালিকা", icon: Package, id: "products" },
  { to: "/stock", label: "স্টক", icon: Boxes, id: "stock" },
  { to: "/customers", label: "বাকির খাতা", icon: BookUser, id: "customer-ledger" },
  { to: "/collection", label: "টাকা আদায়", icon: HandCoins, id: "collection" },
  { to: "/all-customers", label: "সব কাস্টমার", icon: Users, id: "all-customers" },
  { to: "/purchase/new", label: "ক্রয় এন্ট্রি", icon: Truck, id: "purchase-create" },
  { to: "/purchases", label: "ক্রয় ইতিহাস", icon: ClipboardList, id: "purchases" },
  { to: "/companies", label: "কোম্পানি", icon: Building2, id: "companies" },
  { to: "/daily", label: "দৈনিক হিসাব", icon: CalendarDays, id: "daily" },
  { to: "/expenses", label: "খরচ / হিসাব", icon: Wallet, id: "expenses" },
  { to: "/profit", label: "লাভ / ক্ষতি", icon: TrendingUp, id: "profit" },
  { to: "/reports", label: "দিন ক্লোজ রিপোর্ট", icon: FileClock, id: "reports" },
  { to: "/activity", label: "অ্যাক্টিভিটি লগ", icon: History, id: "activity" },
  { to: "/archive", label: "আর্কাইভ", icon: Archive, id: "archive" },
  { to: "/settings", label: "সেটিংস", icon: SettingsIcon, id: "settings" },
];

const MOBILE = [NAV[0], NAV[1], NAV[6], NAV[7]];

function Brand({ dark }: { dark?: boolean }) {
  const { settings } = useSettings();
  return (
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 grid place-items-center shadow-md ring-1 ring-white/10">
        <Zap size={20} className="text-amber-300" fill="currentColor" />
      </span>
      <div className="leading-tight">
        <p className={`font-bold text-[17px] ${dark ? "text-white" : "text-slate-900"}`}>{settings.shop.name}</p>
        <p className={`text-[11px] tracking-[.18em] ${dark ? "text-blue-300" : "text-blue-700"}`}>{settings.shop.name_en}</p>
      </div>
    </div>
  );
}

function NavItem({ n, onClick }: { n: (typeof NAV)[number]; onClick?: () => void }) {
  const Icon = n.icon;
  return (
    <NavLink
      to={n.to}
      end={n.to === "/"}
      onClick={onClick}
      data-testid={`nav-${n.id}-link`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-[15px] font-medium transition-colors ${
          isActive ? "bg-blue-600 text-white shadow-md" : "text-slate-300 hover:bg-slate-800 hover:text-white"
        }`
      }
    >
      <Icon size={18} />
      {n.label}
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [more, setMore] = useState(false);

  const u = user || ({} as any);
  const uName = u.name || "ব্যবহারকারী";
  const uRole = u.role === "owner" ? "মালিক" : (u.role || "অ্যাডমিন");

  return (
    <div className="min-h-screen flex flex-col">
      <header className="w-full no-print bg-[#071a33] border-b border-white/10 shadow-md flex items-center justify-between h-14 md:h-16 px-3 md:px-5" data-testid="app-banner-header">
        <NavLink to="/" aria-label="রানা ইলেক্ট্রিক হোম" className="flex items-center gap-2.5 md:gap-3 min-w-0">
          <span className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center shadow ring-1 ring-white/20 shrink-0">
            <Zap size={20} className="text-[#071a33]" fill="currentColor" />
          </span>
          <div className="leading-tight min-w-0">
            <p className="text-white font-bold text-[15px] md:text-[18px] truncate">{settings.shop.name}</p>
            <p className="text-blue-300/80 text-[10px] md:text-[11px] tracking-wide truncate">{settings.shop.name_en || settings.shop.address}</p>
          </div>
        </NavLink>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden lg:block"><LiveClock /></div>

          <DropdownMenu>
            <DropdownMenuTrigger data-testid="header-avatar-btn" className="flex items-center gap-2 rounded-full md:rounded-xl p-1 md:pl-1.5 md:pr-2.5 md:py-1.5 hover:bg-white/5 md:ring-1 md:ring-white/10 outline-none transition-colors">
              <span className="w-9 h-9 rounded-full bg-blue-600 grid place-items-center ring-2 ring-white/15 shrink-0">
                <User size={18} className="text-white" />
              </span>
              <div className="hidden md:block text-left leading-tight">
                <p className="text-white text-[13px] font-semibold whitespace-nowrap">{uName}</p>
                <p className="text-blue-200/70 text-[11px]">{uRole}</p>
              </div>
              <ChevronDown size={16} className="hidden md:block text-blue-200/70" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="header-avatar-menu">
              <DropdownMenuLabel>
                <p className="font-semibold text-slate-900">{uName}</p>
                <p className="text-xs text-slate-500 font-normal">{u.email || ""}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings" className="cursor-pointer">
                <SettingsIcon size={15} className="mr-2" /> সেটিংস
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="cursor-pointer text-red-600 focus:text-red-600">
                <LogOut size={15} className="mr-2" /> লগআউট
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex flex-1 min-w-0">
        <aside className="hidden md:flex w-64 shrink-0 flex-col bg-slate-900 h-screen sticky top-0 no-print">
          <div className="p-5 border-b border-slate-800 bg-slate-950">
            <Brand dark />
          </div>
          <nav className="flex-1 overflow-y-auto p-3 space-y-1">
            {NAV.map((n) => (
              <NavItem key={n.to} n={n} />
            ))}
          </nav>
          <button onClick={logout} data-testid="logout-button" className="m-3 flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white text-[15px]">
            <LogOut size={18} /> লগআউট
          </button>
        </aside>

        <div className="flex-1 min-w-0 flex flex-col">
          <main className="flex-1 p-4 md:p-6 lg:p-8 pb-24 md:pb-8 max-w-[1500px] w-full mx-auto">
            <Outlet />
          </main>
        </div>
      </div>

      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-md border-t border-slate-200 px-1 py-1.5 flex justify-around z-40 no-print">
        {MOBILE.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`mobile-nav-${n.id}`}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[11px] font-medium px-2 py-1 rounded-lg ${isActive ? "text-blue-700 font-bold" : "text-slate-500"}`}>
              <Icon size={20} />
              {n.label}
            </NavLink>
          );
        })}
        <button onClick={() => setMore(true)} data-testid="mobile-nav-more" className="flex flex-col items-center gap-0.5 text-[11px] font-medium px-2 py-1 text-slate-500">
          <Menu size={20} /> আরও
        </button>
      </nav>

      <Modal open={more} onClose={() => setMore(false)} title="সব মেনু" testid="mobile-more-menu">
        <div className="bg-slate-900 rounded-xl p-2 grid grid-cols-1 gap-1">
          {NAV.map((n) => (
            <NavItem key={n.to} n={n} onClick={() => setMore(false)} />
          ))}
          <button onClick={logout} data-testid="mobile-logout-button" className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 text-[15px]">
            <LogOut size={18} /> লগআউট
          </button>
        </div>
      </Modal>
    </div>
  );
}
